/**
 * Onboarding goal options for Step 2 of the onboarding wizard.
 * Each goal maps to a recommended template shown in Step 3.
 */

export interface OnboardingGoal {
  id: string;
  title: string;
  description: string;
  icon: "wallet" | "satellite-dish" | "bell" | "sparkles" | "search" | "compass";
  /** Template slug or ID to recommend in Step 3. null = show generic explore */
  recommendedTemplateSlug: string | null;
  /** User type for guided onboarding flow (maps to indie/business/engineer configs) */
  userType?: "indie" | "business" | "engineer";
}

export const ONBOARDING_GOALS: OnboardingGoal[] = [
  {
    id: "portfolio",
    title: "Manage my portfolio & trades",
    description: "Track balance, set price alerts, rebalance, claim rewards",
    icon: "wallet",
    recommendedTemplateSlug: "daily-portfolio-sentiment-recap",
    userType: "indie",
  },
  {
    id: "prediction-markets",
    title: "Monitor prediction markets & trade on signals",
    description: "Copy-trade, trade on headlines, follow Polymarket",
    icon: "satellite-dish",
    recommendedTemplateSlug: "polymarket-copy-trade",
    userType: "indie",
  },
  {
    id: "business-ops",
    title: "Automate team or business operations",
    description: "Schedule payments, optimize yield, de-risk on security alerts, log compliance to Slack/QB",
    icon: "bell",
    recommendedTemplateSlug: "multi-wallet-token-send-slack-ping",
    userType: "business",
  },
  {
    id: "build-automations",
    title: "Build automations for any app",
    description: "Wire B3OS into any product, contract, or API you're shipping",
    icon: "sparkles",
    recommendedTemplateSlug: "dev-send-tokens-webhook",
    userType: "engineer",
  },
  {
    id: "research-alpha",
    title: "Research, discover, and trade on alpha",
    description: "Watch whales, scrape trends, execute on signals",
    icon: "search",
    recommendedTemplateSlug: "twitter-trade-the-news",
    userType: "indie",
  },
  {
    id: "exploring",
    title: "Just exploring",
    description: "Show me popular templates to browse",
    icon: "compass",
    recommendedTemplateSlug: null,
  },
];

/** Maximum number of goals a user may pick on Step 2 of the wizard. */
export const MAX_GOAL_SELECTIONS = 3;

/**
 * Canonical id of the v2 wizard's only collected onboarding question.
 * MUST stay in lock-step with the seed in workflow migration 000209 and the
 * Go-side `userGoalQuestionID` const.
 */
export const USER_GOAL_QUESTION_ID = "user_goal";

/** Walkthrough video URL for Step 3 */
export const ONBOARDING_WALKTHROUGH_VIDEO_URL = "https://cdn.b3.fun/walkthrough.mp4";
